import * as React from 'react';
import { api } from '../api';
import { useAsyncAction } from '../hooks';
import { Icon } from '../icons.jsx';
import { Badge, Button, Field, Input } from '../ui.jsx';

const formatPhone = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const STATUS_COPY = {
  pending: {
    label: 'Waiting for confirmation',
    tone: 'wait',
    detail: 'The provider has received your tour request and still needs to confirm it.',
  },
  confirmed: {
    label: 'Tour confirmed',
    tone: 'open',
    detail: 'Your provider confirmed the visit. In a real launch, tour details would be sent by text.',
  },
  waiting: {
    label: 'On the waitlist',
    tone: 'wait',
    detail: 'The provider will contact you when a matching spot may be available.',
  },
  contacted: {
    label: 'Provider contacted you',
    tone: 'brand',
    detail: 'The provider marked that they reached out about a possible opening.',
  },
  placed: {
    label: 'Spot accepted',
    tone: 'open',
    detail: 'The provider marked your family as placed and removed you from the active waitlist.',
  },
};

const FamilyRequestCard = ({ request, go }) => {
  const status = STATUS_COPY[request.status];
  return (
    <article className="family-request-card">
      <span className={`family-request-icon is-${request.kind}`}>
        <Icon name={request.kind === 'tour' ? 'calendar-check' : 'inbox'} size={19}/>
      </span>
      <div className="family-request-main">
        <div className="family-request-heading">
          <div>
            <span>{request.kind === 'tour' ? 'Tour request' : 'Waitlist'}</span>
            <h2>{request.providerName}</h2>
          </div>
          <Badge tone={status.tone} icon={request.status === 'confirmed' ? 'check' : undefined}>
            {status.label}
          </Badge>
        </div>
        <p>{status.detail}</p>
        <dl>
          <div><dt>Age group</dt><dd>{request.ageGroup}</dd></div>
          {request.kind === 'tour' && (
            <>
              <div>
                <dt>Visit</dt>
                <dd>{request.tourType === 'virtual' ? 'Virtual tour' : 'In-person tour'}</dd>
              </div>
              <div><dt>Requested time</dt><dd>{request.date} at {request.time}</dd></div>
            </>
          )}
          <div>
            <dt>{request.kind === 'tour' ? 'Requested' : 'Joined'}</dt>
            <dd>
              {new Intl.DateTimeFormat('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              }).format(new Date(request.createdAt))}
            </dd>
          </div>
        </dl>
        <Button
          variant="ghost"
          size="sm"
          iconRight="chev-right"
          onClick={() => go('profile', { providerId: request.providerId })}
        >
          View provider
        </Button>
      </div>
    </article>
  );
};

export const FamilyRequestsScreen = ({ go }) => {
  const [phone, setPhone] = React.useState('');
  const [requests, setRequests] = React.useState([]);
  const [searched, setSearched] = React.useState(false);
  const lookup = useAsyncAction((value) => api.familyRequests.lookup(value));
  const validPhone = phone.replace(/\D/g, '').length === 10;

  const search = async (value = phone) => {
    const formatted = formatPhone(value);
    if (formatted.replace(/\D/g, '').length !== 10) return;
    setPhone(formatted);
    setSearched(false);
    try {
      setRequests(await lookup.run(formatted));
      setSearched(true);
    } catch {
      setRequests([]);
    }
  };

  return (
    <div className="family-requests-page route-in">
      <section className="family-requests-hero">
        <div className="container">
          <Badge tone="brand">No account needed</Badge>
          <h1>Check your tours and waitlists</h1>
          <p>
            Enter the phone number you used with providers to see your current requests in one
            place.
          </p>
          <form
            className="family-request-search"
            onSubmit={(event) => {
              event.preventDefault();
              search();
            }}
          >
            <Field id="family-request-phone" label="Phone number">
              <Input
                icon="phone"
                value={phone}
                onChange={(event) => setPhone(formatPhone(event.target.value))}
                placeholder="(215) 555-0123"
                inputMode="tel"
                autoComplete="tel"
              />
            </Field>
            <Button
              type="submit"
              variant="primary"
              icon="search"
              disabled={!validPhone || lookup.loading}
            >
              {lookup.loading ? 'Checking…' : 'Check requests'}
            </Button>
          </form>
          <div className="family-request-examples">
            <span>Try the prototype:</span>
            <button type="button" onClick={() => search('(215) 555-0168')}>
              Maya’s tour
            </button>
            <button type="button" onClick={() => search('(215) 555-0134')}>
              Nguyen waitlist
            </button>
          </div>
          <div className="family-request-security">
            <Icon name="shield" size={17}/>
            <div>
              <strong>Prototype privacy note</strong>
              <span>
                A real launch would text a one-time verification code before showing family
                records. This demonstration skips that delivery step.
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="family-requests-results">
        <div className="container">
          {lookup.error && (
            <div className="family-request-error" role="alert">
              We couldn’t check your requests: {lookup.error.message}
            </div>
          )}
          {searched && requests.length === 0 && (
            <div className="family-request-empty">
              <Icon name="search" size={24}/>
              <h2>No matching requests</h2>
              <p>Check the phone number or contact the provider directly.</p>
              <Button variant="secondary" onClick={() => go('results')}>Browse providers</Button>
            </div>
          )}
          {requests.length > 0 && (
            <>
              <div className="family-results-heading">
                <div>
                  <span>Requests for {phone}</span>
                  <h2>{requests.length} active {requests.length === 1 ? 'record' : 'records'}</h2>
                </div>
                <Button variant="secondary" size="sm" onClick={() => go('results')}>
                  Find another provider
                </Button>
              </div>
              <div className="family-request-list">
                {requests.map((request) => (
                  <FamilyRequestCard key={`${request.kind}-${request.id}`} request={request} go={go}/>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};
